/**
 * WCAG 2.x contrast ratio and APCA Lc, measured on the same encoded channels
 * the export / Figma pipeline stores.
 *
 * Ground truth (matches Figma QA):
 * - P3: Display-P3 floats rounded like export (6 decimals). WCAG linearizes
 *   those 0–1 bytes as if they were sRGB (Figma "Color contrast" plugin).
 *   APCA uses displayP3toY on the same floats (Polychrom / apca-w3).
 * - sRGB: 8-bit hex channels. WCAG and APCA both use 8-bit sRGB.
 *
 * Alpha is composited in encoded (gamma) space, then measured — no extra
 * 8-bit flatten on P3, because the plugin reads variable/fill floats.
 *
 * @see https://www.w3.org/TR/WCAG22/#dfn-contrast-ratio
 */

import { APCAcontrast, displayP3toY, sRGBtoY } from 'apca-w3';
import { parseColor, getP3RgbFloats } from './colorUtils.js';

function srgbChannelToLinearByte(c255) {
  const c = c255 / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function srgbChannelToLinearFloat(c) {
  const x = Math.max(0, Math.min(1, c));
  return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
}

function clamp01(n) {
  return Math.max(0, Math.min(1, n));
}

/** Quantize a 0–1 channel to 8-bit (sRGB hex / framebuffer). */
export function quantize8(value) {
  return Math.round(clamp01(value) * 255) / 255;
}

/** Same 6-decimal rounding the Figma export writes for P3 floats. */
export function quantizeP3Export(value) {
  return Math.round(clamp01(value) * 1e6) / 1e6;
}

function quantizeForSpace(ch, colorSpace) {
  if (!ch) return null;
  const alphaRaw = ch.alpha == null ? 1 : ch.alpha;
  if (colorSpace === 'srgb') {
    return {
      r: quantize8(ch.r),
      g: quantize8(ch.g),
      b: quantize8(ch.b),
      alpha: alphaRaw >= 1 ? 1 : quantize8(alphaRaw)
    };
  }
  return {
    r: quantizeP3Export(ch.r),
    g: quantizeP3Export(ch.g),
    b: quantizeP3Export(ch.b),
    alpha: alphaRaw >= 1 ? 1 : quantizeP3Export(alphaRaw)
  };
}

/**
 * Encoded (gamma) channel bytes + alpha in the workspace color space.
 *
 * - sRGB workspace: sRGB-clamped channels (`parseColor(...).rgb`).
 * - P3 workspace: Display-P3 channels (`getP3RgbFloats`).
 *
 * WCAG always uses the sRGB luminance formula. In P3 mode we still feed it
 * Display-P3 bytes (treated as sRGB) so the ratio matches the common
 * non-P3-aware Figma "Color contrast" plugin.
 */
function encodedChannelsFromParsed(parsed, colorSpace) {
  if (!parsed?.isValid) return null;
  if (colorSpace === 'p3') {
    const f = getP3RgbFloats(parsed);
    if (!f) return null;
    return { r: f.r, g: f.g, b: f.b, alpha: f.alpha ?? 1 };
  }
  if (!parsed.rgb) return null;
  return {
    r: clamp01(parsed.rgb.r ?? 0),
    g: clamp01(parsed.rgb.g ?? 0),
    b: clamp01(parsed.rgb.b ?? 0),
    alpha: (parsed.alpha != null) ? parsed.alpha : (parsed.rgb.alpha ?? 1)
  };
}

function encodedChannelsFromInput(input, colorSpace) {
  if (input && typeof input === 'object' && input.isValid) {
    return encodedChannelsFromParsed(input, colorSpace);
  }
  if (typeof input !== 'string') return null;
  const parsed = parseColor(input, colorSpace === 'p3' ? 'p3' : 'srgb');
  return encodedChannelsFromParsed(parsed, colorSpace);
}

/**
 * Composite translucent fg over opaque bg in encoded (gamma) space, matching
 * apca-w3 `alphaBlend` / CSS / Figma normal opacity.
 */
function flattenFgOverBg(fgCh, bgCh, colorSpace) {
  const fg = quantizeForSpace(fgCh, colorSpace);
  const bg = quantizeForSpace(bgCh, colorSpace);
  if (!fg || !bg) return { fg: null, bg: null };
  const a = fg.alpha;
  const blended = a >= 1
    ? { r: fg.r, g: fg.g, b: fg.b, alpha: 1 }
    : a <= 0
      ? { r: bg.r, g: bg.g, b: bg.b, alpha: 1 }
      : {
          r: bg.r + (fg.r - bg.r) * a,
          g: bg.g + (fg.g - bg.g) * a,
          b: bg.b + (fg.b - bg.b) * a,
          alpha: 1
        };
  return { fg: quantizeForSpace(blended, colorSpace), bg };
}

function wcagFromChannels(fgCh, bgCh, colorSpace) {
  const lin = colorSpace === 'srgb'
    ? (c) => srgbChannelToLinearByte(Math.round(c * 255))
    : srgbChannelToLinearFloat;
  const Lfg = 0.2126 * lin(fgCh.r) + 0.7152 * lin(fgCh.g) + 0.0722 * lin(fgCh.b);
  const Lbg = 0.2126 * lin(bgCh.r) + 0.7152 * lin(bgCh.g) + 0.0722 * lin(bgCh.b);
  const lighter = Math.max(Lfg, Lbg);
  const darker = Math.min(Lfg, Lbg);
  return (lighter + 0.05) / (darker + 0.05);
}

function apcaFromChannels(fgCh, bgCh, colorSpace) {
  if (colorSpace === 'p3') {
    const fgY = displayP3toY([fgCh.r, fgCh.g, fgCh.b]);
    const bgY = displayP3toY([bgCh.r, bgCh.g, bgCh.b]);
    return Math.abs(APCAcontrast(fgY, bgY));
  }
  const fgY = sRGBtoY([
    Math.round(fgCh.r * 255),
    Math.round(fgCh.g * 255),
    Math.round(fgCh.b * 255)
  ]);
  const bgY = sRGBtoY([
    Math.round(bgCh.r * 255),
    Math.round(bgCh.g * 255),
    Math.round(bgCh.b * 255)
  ]);
  return Math.abs(APCAcontrast(fgY, bgY));
}

function contrastFromFlattened(fgCh, bgCh, algorithm, colorSpace) {
  if (!fgCh || !bgCh) return NaN;
  if (algorithm === 'wcag') return wcagFromChannels(fgCh, bgCh, colorSpace);
  return apcaFromChannels(fgCh, bgCh, colorSpace === 'p3' ? 'p3' : 'srgb');
}

/**
 * @param {string} input
 * @param {'srgb' | 'p3'} [colorSpace='p3']
 */
export function relativeLuminanceFromHex(input, colorSpace = 'p3') {
  const space = colorSpace === 'srgb' ? 'srgb' : 'p3';
  const ch = quantizeForSpace(encodedChannelsFromInput(input, space), space);
  if (!ch) return 0;
  const lin = space === 'srgb'
    ? (c) => srgbChannelToLinearByte(Math.round(c * 255))
    : srgbChannelToLinearFloat;
  return 0.2126 * lin(ch.r) + 0.7152 * lin(ch.g) + 0.0722 * lin(ch.b);
}

/**
 * WCAG contrast ratio on exported Figma channels.
 *
 * @param {string} fgInput
 * @param {string} bgInput
 * @param {'srgb' | 'p3'} [colorSpace='p3']
 */
export function wcagContrastRatio(fgInput, bgInput, colorSpace = 'p3') {
  return measureContrastPair(fgInput, bgInput, 'wcag', colorSpace);
}

/**
 * Measure APCA Lc or WCAG CR for a pair on the exported channel representation.
 *
 * @param {string|Object} fgInput - CSS color string or a `parseColor` result
 * @param {string|Object} bgInput
 * @param {'apca' | 'wcag'} algorithm
 * @param {'srgb' | 'p3'} [colorSpace='p3']
 */
export function measureContrastPair(fgInput, bgInput, algorithm, colorSpace = 'p3') {
  const space = colorSpace === 'srgb' ? 'srgb' : 'p3';
  const fgRaw = encodedChannelsFromInput(fgInput, space);
  const bgRaw = encodedChannelsFromInput(bgInput, space);
  const { fg, bg } = flattenFgOverBg(fgRaw, bgRaw, space);
  return contrastFromFlattened(fg, bg, algorithm === 'wcag' ? 'wcag' : 'apca', space);
}

/**
 * Pick the color string for measurement given the workspace color space.
 *
 * P3: full-precision OKLCH so wide-gamut chroma is not sRGB-clamped before
 * `parseColor` / `getP3RgbFloats`. sRGB: 8-bit hex, matching export.
 *
 * @param {Object|null} colorData
 * @param {'srgb' | 'p3'} colorSpace
 */
export function colorInputForMeasurement(colorData, colorSpace = 'p3') {
  if (colorSpace === 'p3' && colorData && colorData.oklch) {
    return formatOklchForMeasure(colorData.oklch);
  }
  return (colorData && colorData.hex) || '#000000';
}

/**
 * Format a culori OKLCH object as a CSS `oklch(...)` string.
 * Full numeric precision — do not use for display labels.
 *
 * @param {{l?: number, c?: number, h?: number, alpha?: number}} oklch
 */
export function formatOklchForMeasure(oklch) {
  if (!oklch) return '#000000';
  const l = oklch.l ?? 0;
  const c = oklch.c ?? 0;
  const h = oklch.h ?? 0;
  const alpha = oklch.alpha;
  const alphaStr = (alpha != null && alpha < 1) ? ` / ${alpha}` : '';
  return `oklch(${l} ${c} ${h}${alphaStr})`;
}

/**
 * Display / target precision: integer Lc for APCA, one decimal for WCAG CR.
 * @param {number} value
 * @param {'apca' | 'wcag'} algorithm
 */
export function roundContrastLevel(value, algorithm) {
  if (typeof value !== 'number' || !isFinite(value)) return value;
  if (algorithm === 'wcag') return Math.round(value * 10) / 10;
  return Math.round(value);
}

/**
 * Whether a measured value meets the user's target in QA terms.
 * WCAG is a minimum (raw ratio). APCA Perfect is integer-rounded Lc.
 */
export function meetsContrastTarget(value, target, algorithm) {
  if (typeof value !== 'number' || !isFinite(value)) return false;
  if (algorithm === 'wcag') return value >= target;
  return Math.round(value) >= target;
}
